import { useRef, useCallback, useEffect, useState } from 'react';
import {
  calculateVelocity,
  calculateNextFrame,
  shouldTriggerMomentum,
  throttle
} from '../utils/scrollPhysics';
import {
  getOptimizedPhysicsConfig,
  prefersReducedMotion,
  requestAnimFrame,
  cancelAnimFrame,
  getPassiveEventListenerOptions
} from '../utils/performanceUtils';

/**
 * Smooth scrolling hook with momentum and physics
 * @param {Object} options - Configuration options
 * @param {string} options.direction - 'vertical' or 'horizontal'
 * @param {Function} options.onScroll - Callback for scroll updates
 * @param {Function} options.onMomentumStart - Callback when momentum starts
 * @param {Function} options.onMomentumEnd - Callback when momentum ends
 * @param {boolean} options.disabled - Disable smooth scrolling
 * @param {Object} options.bounds - {min, max} scroll boundaries
 * @returns {Object} - Event handlers and scroll state
 */
export const useSmoothScroll = ({
  direction = 'vertical',
  onScroll = null,
  onMomentumStart = null,
  onMomentumEnd = null,
  disabled = false,
  bounds = { min: 0, max: 1000 }
}) => {
  // Get optimized physics config based on device capabilities
  const PHYSICS_CONFIG = getOptimizedPhysicsConfig();

  // Check if smooth scrolling should be disabled
  const shouldDisableSmooth = disabled || prefersReducedMotion();

  // State for tracking scroll behavior
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMomentumScrolling, setIsMomentumScrolling] = useState(false);

  // Emergency stop timeout ref
  const emergencyStopRef = useRef(null);

  // Refs for tracking pointer movement and animation
  const pointerTrackingRef = useRef({
    isPointerDown: false,
    points: [],
    lastPosition: { x: 0, y: 0 },
    startPosition: { x: 0, y: 0 }
  });

  const animationRef = useRef({
    rafId: null,
    velocity: 0,
    position: 0,
    isAnimating: false
  });

  const scrollContainerRef = useRef(null);

  // Property mapping based on direction
  const isVertical = direction === 'vertical';
  const positionProp = isVertical ? 'y' : 'x';
  const clientProp = isVertical ? 'clientY' : 'clientX';

  /**
   * Add a tracking point for velocity calculation
   */
  const addTrackingPoint = useCallback((x, y) => {
    const tracking = pointerTrackingRef.current;
    const point = { x, y, timestamp: Date.now() };

    tracking.points.push(point);

    // Keep only recent points for performance
    if (tracking.points.length > PHYSICS_CONFIG.PERFORMANCE.MAX_POINTS) {
      tracking.points.shift();
    }

    // Clean up old points outside time window
    const now = Date.now();
    tracking.points = tracking.points.filter(
      p => (now - p.timestamp) <= PHYSICS_CONFIG.PERFORMANCE.TIME_WINDOW
    );
  }, []);

  /**
   * Start momentum scrolling animation
   */
  const startMomentumScroll = useCallback((initialVelocity) => {
    if (shouldDisableSmooth || !shouldTriggerMomentum(initialVelocity)) {
      return;
    }

    setIsMomentumScrolling(true);
    onMomentumStart?.();

    const animation = animationRef.current;
    animation.velocity = initialVelocity;
    animation.isAnimating = true;

    // Add frame counter to prevent infinite loops
    let frameCount = 0;
    const MAX_FRAMES = 300; // Max 300 frames (5 seconds at 60fps)

    // Set emergency stop timeout (max 2 seconds - reduced)
    emergencyStopRef.current = setTimeout(() => {
      console.warn('Emergency stop triggered - momentum scrolling lasted too long');
      animation.isAnimating = false;
      setIsMomentumScrolling(false);
      onMomentumEnd?.();
    }, 2000);

    const animate = () => {
      if (!animation.isAnimating) return;

      // Frame counter safety check
      frameCount++;
      if (frameCount > MAX_FRAMES) {
        console.warn('Animation stopped due to max frames reached');
        animation.isAnimating = false;
        setIsMomentumScrolling(false);
        onMomentumEnd?.();
        return;
      }

      const nextFrame = calculateNextFrame(
        { position: animation.position, velocity: animation.velocity },
        bounds
      );

      animation.position = nextFrame.position;
      animation.velocity = nextFrame.velocity;

      // Call scroll callback with new position
      onScroll?.(animation.position, {
        velocity: animation.velocity,
        isMomentum: true,
        atBoundary: nextFrame.atBoundary
      });

      if (nextFrame.shouldStop) {
        animation.isAnimating = false;
        setIsMomentumScrolling(false);

        // Clear emergency stop timeout
        if (emergencyStopRef.current) {
          clearTimeout(emergencyStopRef.current);
          emergencyStopRef.current = null;
        }

        onMomentumEnd?.();
        return;
      }

      animation.rafId = requestAnimFrame(animate);
    };

    animation.rafId = requestAnimFrame(animate);
  }, [disabled, bounds, onScroll, onMomentumStart, onMomentumEnd]);

  /**
   * Stop any ongoing momentum animation
   */
  const stopMomentumScroll = useCallback(() => {
    const animation = animationRef.current;

    if (animation.rafId) {
      cancelAnimFrame(animation.rafId);
      animation.rafId = null;
    }

    // Clear emergency stop timeout
    if (emergencyStopRef.current) {
      clearTimeout(emergencyStopRef.current);
      emergencyStopRef.current = null;
    }

    animation.isAnimating = false;
    setIsMomentumScrolling(false);
  }, []);

  /**
   * Get pointer position from mouse or touch event
   */
  const getPointerPosition = useCallback((event) => {
    // Handle touch events
    if (event.touches && event.touches.length > 0) {
      return {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }
    // Handle mouse events
    return {
      x: event.clientX,
      y: event.clientY
    };
  }, []);

  /**
   * Handle pointer down (mouse down / touch start)
   */
  const handlePointerDown = useCallback((event) => {
    if (shouldDisableSmooth) return;

    // Stop any ongoing momentum immediately
    stopMomentumScroll();

    // If momentum was scrolling, prevent further interaction for this event
    if (isMomentumScrolling) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const tracking = pointerTrackingRef.current;
    const position = getPointerPosition(event);

    tracking.isPointerDown = true;
    tracking.startPosition = position;
    tracking.lastPosition = position;
    tracking.points = [{ ...position, timestamp: Date.now() }];

    setIsScrolling(true);

    // Prevent default to avoid native scrolling
    event.preventDefault();
  }, [shouldDisableSmooth, stopMomentumScroll, isMomentumScrolling, getPointerPosition]);

  /**
   * Handle pointer move (mouse move / touch move)
   */
  const handlePointerMove = useCallback((event) => {
    if (shouldDisableSmooth) return;

    const tracking = pointerTrackingRef.current;
    if (!tracking.isPointerDown) return;

    const currentPosition = getPointerPosition(event);

    // Calculate delta movement
    const delta = isVertical
      ? currentPosition.y - tracking.lastPosition.y
      : currentPosition.x - tracking.lastPosition.x;

    // Scale down the immediate movement to prevent jumpy behavior
    const scaledDelta = delta * 1.0; // Full movement for responsive dragging

    // Update animation position for immediate feedback
    const animation = animationRef.current;
    animation.position -= scaledDelta; // Invert for natural scroll direction

    // Add tracking point
    addTrackingPoint(currentPosition.x, currentPosition.y);

    // Call scroll callback for immediate feedback
    onScroll?.(animation.position, {
      velocity: 0,
      isMomentum: false,
      isDragging: true
    });

    tracking.lastPosition = currentPosition;

    // Prevent default to avoid native scrolling
    event.preventDefault();
  }, [shouldDisableSmooth, isVertical, addTrackingPoint, onScroll, getPointerPosition]);

  /**
   * Handle pointer up (mouse up / touch end)
   */
  const handlePointerUp = useCallback((event) => {
    if (shouldDisableSmooth) return;

    const tracking = pointerTrackingRef.current;
    if (!tracking.isPointerDown) return;

    tracking.isPointerDown = false;
    setIsScrolling(false);

    // Calculate final velocity
    const velocity = calculateVelocity(tracking.points);
    const finalVelocity = isVertical ? -velocity.y : -velocity.x; // Invert for natural scroll direction

    // Start momentum scrolling if velocity is sufficient
    if (Math.abs(finalVelocity) >= PHYSICS_CONFIG.VELOCITY.MIN_FOR_MOMENTUM) {
      startMomentumScroll(finalVelocity);
    } else {
      // No momentum - trigger momentum end immediately for snapping
      onMomentumEnd?.();
    }

    // Clear tracking data
    tracking.points = [];

    event.preventDefault();
  }, [shouldDisableSmooth, isVertical, startMomentumScroll, onMomentumEnd]);

  /**
   * Throttled pointer move handler for performance
   */
  const throttledPointerMove = useCallback(
    throttle(handlePointerMove, PHYSICS_CONFIG.PERFORMANCE.THROTTLE_MS),
    [handlePointerMove]
  );

  /**
   * Handle wheel events for desktop
   */
  const handleWheel = useCallback((event) => {
    if (shouldDisableSmooth || isMomentumScrolling) return;

    const delta = isVertical ? event.deltaY : event.deltaX;
    const animation = animationRef.current;

    animation.position += delta * 0.5; // Scale wheel input

    onScroll?.(animation.position, {
      velocity: delta,
      isMomentum: false,
      isWheel: true
    });

    event.preventDefault();
  }, [shouldDisableSmooth, isVertical, isMomentumScrolling, onScroll]);

  /**
   * Set scroll position programmatically
   */
  const setScrollPosition = useCallback((position, smooth = true) => {
    const animation = animationRef.current;

    if (smooth && !shouldDisableSmooth) {
      // Stop current momentum and animate to position
      stopMomentumScroll();

      const targetVelocity = (position - animation.position) * 0.1;
      animation.position = position;
      startMomentumScroll(targetVelocity);
    } else {
      // Immediate position change
      animation.position = position;
      onScroll?.(position, { velocity: 0, isMomentum: false });
    }
  }, [shouldDisableSmooth, stopMomentumScroll, startMomentumScroll, onScroll]);

  /**
   * Get current scroll position
   */
  const getScrollPosition = useCallback(() => {
    return animationRef.current.position;
  }, []);

  /**
   * Handle escape key to stop momentum scrolling
   */
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && isMomentumScrolling) {
      stopMomentumScroll();
    }
  }, [isMomentumScrolling, stopMomentumScroll]);

  /**
   * Setup keyboard listener and cleanup on unmount
   */
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      stopMomentumScroll();
    };
  }, [stopMomentumScroll, handleKeyDown]);

  /**
   * Attach native event listeners with passive: false to allow preventDefault
   */
  const attachEventListeners = useCallback((element) => {
    if (!element || shouldDisableSmooth) return;

    scrollContainerRef.current = element;

    // Options for non-passive event listeners
    const options = { passive: false };

    // Mouse events
    element.addEventListener('mousedown', handlePointerDown, options);
    element.addEventListener('mousemove', throttledPointerMove, options);
    element.addEventListener('mouseup', handlePointerUp, options);
    element.addEventListener('mouseleave', handlePointerUp, options);

    // Touch events (critical to be non-passive)
    element.addEventListener('touchstart', handlePointerDown, options);
    element.addEventListener('touchmove', throttledPointerMove, options);
    element.addEventListener('touchend', handlePointerUp, options);
    element.addEventListener('touchcancel', handlePointerUp, options);

    // Wheel event
    element.addEventListener('wheel', handleWheel, options);

    console.log('[useSmoothScroll] Attached non-passive event listeners to element');
  }, [handlePointerDown, throttledPointerMove, handlePointerUp, handleWheel, shouldDisableSmooth]);

  /**
   * Detach all event listeners
   */
  const detachEventListeners = useCallback((element) => {
    if (!element) return;

    // Remove all listeners
    element.removeEventListener('mousedown', handlePointerDown);
    element.removeEventListener('mousemove', throttledPointerMove);
    element.removeEventListener('mouseup', handlePointerUp);
    element.removeEventListener('mouseleave', handlePointerUp);
    element.removeEventListener('touchstart', handlePointerDown);
    element.removeEventListener('touchmove', throttledPointerMove);
    element.removeEventListener('touchend', handlePointerUp);
    element.removeEventListener('touchcancel', handlePointerUp);
    element.removeEventListener('wheel', handleWheel);

    console.log('[useSmoothScroll] Detached event listeners from element');
  }, [handlePointerDown, throttledPointerMove, handlePointerUp, handleWheel]);

  return {
    // Ref callback to attach listeners
    ref: attachEventListeners,

    // Manual attach/detach functions
    attachEventListeners,
    detachEventListeners,

    // Scroll state
    isScrolling,
    isMomentumScrolling,

    // Control functions
    setScrollPosition,
    getScrollPosition,
    stopMomentumScroll,

    // Ref for the scroll container
    scrollContainerRef
  };
};